import { apiClient, API_PATHS } from '@/services/api';

class UserGroupService {
    /**
     * Get available user groups for the current user
     */
    async getAvailableUserGroups() {
        const response = await apiClient.rfp.get(API_PATHS.rfp.user.availableUserGroups);
        return response.data;
    }

    /**
     * Switch to a different user group
     * @param {string} userGroupId - The ID of the user group to switch to
     * @param {string[]} selectedRoles - Optional: roles to use (for RYD users)
     * @param {string[]} selectedPartnerTypes - Optional: partner types to use (for RYD users)
     */
    async switchUserGroup(userGroupId, selectedRoles, selectedPartnerTypes) {
        const payload = { userGroupId };
        if (selectedRoles && selectedRoles.length > 0) {
            payload.selectedRoles = selectedRoles;
        }
        if (selectedPartnerTypes && selectedPartnerTypes.length > 0) {
            payload.selectedPartnerTypes = selectedPartnerTypes;
        }

        const response = await apiClient.rfp.post(
            API_PATHS.rfp.user.switchUserGroup,
            payload
        );
        return response.data;
    }

    /**
     * Re-derives partnerSettings/navigationPreferences after a switch — only `/auth/me` returns them.
     * @param {Object} userData - userData from the switch response
     */
    async fetchEnrichedUserData(userData) {
        try {
            const { data } = await apiClient.rfp.get(API_PATHS.rfp.auth.me);
            return { ...userData, ...data };
        } catch (error) {
            console.warn('[UserGroup] Failed to re-derive user data from /auth/me:', error);
            return userData;
        }
    }

    /**
     * Current user data as the backend sees it now (roles, partner types, partnerSettings).
     * Unlike fetchEnrichedUserData this throws, so an on-demand refresh can report a failure.
     */
    async fetchCurrentUserData() {
        const { data } = await apiClient.rfp.get(API_PATHS.rfp.auth.me);
        return data;
    }

    /**
     * Fetch partner names
     */
    async fetchPartnerNames() {
        try {
            const response = await apiClient.rfp.get(API_PATHS.rfp.partnerConfigs.list);

            // Ensure data is an array before processing
            if (!Array.isArray(response.data)) {
                console.error('Unexpected response format:', response.data);
                return [];
            }

            // Map, filter out empty partner names, and sort alphabetically
            return response.data
                .map(partner => ({
                    partnerName: partner.partnerName?.trim() || partner.name?.trim() || '',
                    identifierInDataWarehouse: partner.identifierInDataWarehouse,
                    apiIntegrationVersion: partner.apiIntegrationVersion,
                    sponsors: Array.isArray(partner.sponsors) ? partner.sponsors : [],
                    id: partner.id,
                    // Use partnerName for name property for compatibility with autocomplete
                    name: partner.partnerName?.trim() || partner.name?.trim() || ''
                }))
                .filter(partner => partner.partnerName !== '') // Remove empty or whitespace-only names
                .sort((a, b) => a.partnerName.localeCompare(b.partnerName, 'en', { sensitivity: 'base' })); // Sort alphabetically

        } catch (error) {
            console.error('Error fetching partner names:', error);
            throw error;
        }
    }

    /**
     * Update local storage with new user data after switching
     * @param {Object} userData - Updated user data
     */
    updateLocalStorage(userData) {
        localStorage.setItem('userAddData', JSON.stringify(userData));
        // Trigger storage event for other tabs
        window.dispatchEvent(new Event('storage'));
    }
}

export const userGroupService = new UserGroupService();
